import { useState } from 'react';

import { BackLink } from '@/components/BackLink';
import { Button } from '@/components/Button';
import { Callout } from '@/components/Callout';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { Spinner } from '@/components/Spinner';
import { StatusBadge } from '@/components/StatusBadge';
import { LANGUAGE } from '@/i18n/dictionary';
import { useTranslation } from '@/i18n/useTranslation';
import { useKioskSession } from '@/lib/kioskSession';
import { HANDOFF_CONTROL } from '@/services/handoff.contract';
import { useBack } from '@/shared/hooks/useBack';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

import { useAssociateStatus } from '../hooks/useAssociateStatus';
import type { AssociateStatus } from '../types';
import { ASSIGNMENT_KIND } from '../types';
import { DetailList } from './DetailList';

/**
 * The card's date in the kiosk's language [ISS-144].
 *
 * `dateValue` is a calendar day (`2026-08-05`, UTC) and is formatted the way `DateCalendar` formats its days —
 * `Intl`, UTC, so the device's clock and zone can never move it to a different day. The api's `date` is en-US
 * and stays the fallback: an api that predates `dateValue`, a record with no date, or a value that is not a
 * real calendar day (malformed, out of range, or one that would roll over) all render what the api wrote.
 */
function cardDate(card: AssociateStatus, locale: string): string {
  const value = card.dateValue ?? '';
  const day = new Date(`${value}T00:00:00Z`);
  // Not a date at all (`''`, `08/05/2026`, `2026-13-01`): `Intl` would throw a RangeError and take the card down.
  if (Number.isNaN(day.getTime())) return card.date;
  // Parseable but not the day it names (`2026-02-30` rolls over to Mar 2). Also the shape check: only an exact
  // `YYYY-MM-DD` round-trips, so however an engine parses a non-ISO string, it cannot reach `Intl`.
  if (day.toISOString().slice(0, 10) !== value) return card.date;
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(day);
}

/**
 * The assignment / submission status card, and the portal deflection on it
 * [KAS-1, KAS-2; Figma · 14 · Active associate, all three viewports].
 *
 * **One card, two states.** A live placement shows the person's next shift; a submission shows their FIRST
 * DAY and frames the whole card as what they applied for. Only the date's LABEL changes — telling somebody
 * who has not been placed that they have an assignment is a promise the kiosk cannot keep.
 *
 * **No pay, rate or payday, and the guarantee is structural**: `AssociateStatus` has no such field, so there
 * is nothing here to render even by accident [KAS-1 § the narrowing IS the contract; decision 0071].
 *
 * **Reaching this card by asking creates no queue entry.** The other end of that rule lives in KID-5, which
 * does not emit for `reconfirmAssignment`; it is asserted from both ends deliberately, because a rule kept in
 * one place is a rule the other place can quietly break.
 *
 * KAS-2's deflection is the callout and the scan block: an active associate asking about hours, pay or
 * personal details is pointed at the talent portal on their own phone rather than served here, because this
 * is a shared kiosk in a public lobby and it stays read-only.
 */
export function AssociateStatusScreen() {
  const [portalHelpSent, setPortalHelpSent] = useState(false);
  const t = useTranslation();
  const back = useBack();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const cards = useKioskSession((state) => state.associateCards);
  const endCandidateSession = useKioskSession((state) => state.endCandidateSession);
  const summonRepresentative = useKioskSession((state) => state.summonRepresentative);

  const language = useKioskSession((state) => state.language);
  // The same locale choice `DateCalendar` makes, so every date on the kiosk reads in one convention.
  const locale = language === LANGUAGE.es ? 'es' : 'en-US';

  const { loading } = useAssociateStatus();

  /*
   * The wait replaces the screen rather than dimming it, the same way the identity lookup's does: a card
   * half-rendered with empty rows reads as an assignment with no client and no address.
   */
  if (loading) {
    return (
      <KioskScreen>
        <KioskCard header={<StatusBadge label={t('badge.kiosk')} />}>
          <div
            aria-busy="true"
            aria-live="polite"
            className="flex w-full flex-col items-center gap-4 py-12"
          >
            <Spinner />
            <p className="text-sm leading-6 text-[var(--muted-foreground)]">
              {t('associate.loading')}
            </p>
          </div>
        </KioskCard>
      </KioskScreen>
    );
  }

  /*
   * No card, no screen. The status is assembled by the api and set before this renders; if it is missing,
   * something upstream failed and inventing an empty card would tell a candidate their assignment has no
   * client and no address — worse than routing them to a person.
   */
  if (!cards || cards.length === 0) {
    return (
      <KioskScreen>
        <KioskCard header={<StatusBadge label={t('badge.kiosk')} />}>
          <div className="flex w-full flex-col items-start gap-6 pb-8">
            <h1
              className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
              ref={heading}
              tabIndex={-1}
            >
              {t('routing.assistance.heading')}
            </h1>
            <p className="w-full text-sm leading-6 text-[var(--muted-foreground)]">
              {t('routing.assistance.body')}
            </p>
          </div>
        </KioskCard>
      </KioskScreen>
    );
  }

  /*
   * **One card, or a list of them** [Operator, 2026-09-08]. `03-us-kas-1` recorded multiples as unspecified
   * — *"Whether a person with two live submissions sees two cards, a list, or the most recent, was never
   * raised"* — and the ruling is a list.
   *
   * A placement is always alone: the api returns it that way (BR-K6), so `several` can only ever be
   * submissions. That is why the plural subheading talks about applications and not about assignments.
   */
  const several = cards.length > 1;
  const [first] = cards;
  const isPlacement = first!.kind === ASSIGNMENT_KIND.placement;

  return (
    <KioskScreen>
      <KioskCard
        footer={
          <Button fullWidth onClick={endCandidateSession} variant="secondary">
            {t('associate.done')}
          </Button>
        }
        header={
          <div className="flex w-full items-center justify-between">
            <BackLink onClick={back} />
            <StatusBadge label={t('badge.kiosk')} />
          </div>
        }
      >
        <div className="flex w-full flex-col items-start gap-6 pb-8">
          <div className="flex w-full flex-col items-start gap-2">
            <h1
              className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
              ref={heading}
              tabIndex={-1}
            >
              {/*
                The frame reads "Welcome back, Maria" and this renders the bare greeting, because the name
                is DATA the kiosk does not have: KID-2's diagnosis response carries a diagnosis, a candidate
                id and an email-on-file, and deliberately nothing else — the narrowest payload that answers
                the question was the point of that story. Adding a first name is a contract change to a
                shipped endpoint and belongs to whoever makes it, not to this screen. Reported, not invented.
              */}
              {t('associate.heading')}
            </h1>
            <p className="w-full text-sm leading-6 text-[var(--muted-foreground)]">
              {several
                ? t('associate.subheading.applications', { count: String(cards.length) })
                : isPlacement
                  ? t('associate.subheading')
                  : t('associate.subheading.applied')}
            </p>
          </div>

          {/*
            The rows are identical per card; what a list adds is a heading per application, because four
            unlabelled blocks of client/location/shift/date are unreadable stacked. A single card keeps no
            heading — it would repeat the client name that is already its first row.
          */}
          {cards.map((card, index) => (
            <div
              className="flex w-full flex-col items-start gap-2"
              key={`${card.clientName}-${index}`}
            >
              {several && (
                <h2 className="w-full text-base font-semibold leading-6 text-black">
                  {t('associate.application', { n: String(index + 1) })}
                </h2>
              )}
              <DetailList
                rows={[
                  { label: t('associate.client'), value: card.clientName },
                  // The address alone while the api sends an empty site [Operator ruling 28, 2026-09-29:
                  // "Address only" — Bullhorn holds no site name, and the order title stood in for one]. An
                  // api that still sends a site keeps "site · address"; an empty one never leaves a stray
                  // separator [ISS-144].
                  {
                    label: t('associate.location'),
                    value: [card.siteName, card.streetAddress].filter(Boolean).join(' · '),
                  },
                  { label: t('associate.shift'), value: card.shift },
                  {
                    // The one label that follows the state. Everything else on the card is identical.
                    label:
                      card.kind === ASSIGNMENT_KIND.placement
                        ? t('associate.nextShift')
                        : t('associate.firstDay'),
                    value: cardDate(card, locale),
                  },
                ]}
              />
            </div>
          ))}

          {/*
            The card NAMES the talent portal and no longer offers a control to reach it
            [decision 0114, approved 2026-08-25]. What sat here was the onward CTA — the portal's address,
            rendered as text because this repository has no QR encoder. 0114 removes the CTA itself, not
            just its QR form: "no QR code and no live redirect on any screen". KAS-1's own table now reads
            "⛔ ~~a QR to the talent portal~~ — REMOVED", and its frontend row bans rendering it.

            The deflection text stays, and that is the decision's own scope: the card still tells a placed
            associate that pay, tax and personal changes happen in the talent portal on their own phone.
            ⚠️ HOW they get there is now unspecified — 0114 § *What is NOT decided*.
          */}
          {/*
            The callout carries an ESCALATION, which the frame draws and the build did not have.
            
            KAS-2 forbids offering a control to REACH the portal, and 0114 forbids any QR or live redirect.
            This is neither: it enqueues a recruiter — "let us know and we'll notify an Integrity rep" is the
            frame's own promise — and the kiosk still holds no portal address and performs no portal
            function. An associate who cannot get into the portal had, until now, nothing to do but leave.
            
            One-way and once: the label is replaced by a confirmation rather than staying tappable, because
            a second tap would put the same person on the queue twice and a recruiter cannot tell those apart.
          */}
          <Callout
            action={
              portalHelpSent ? (
                <span className="block py-[13px] text-sm leading-5 text-[var(--primary-muted-foreground)]">
                  {t('associate.portalDeflection.done')}
                </span>
              ) : (
                <button
                  className="kiosk-focusable py-[13px] text-left text-sm leading-5 text-[var(--link)] underline"
                  onClick={() => {
                    setPortalHelpSent(true);
                    /*
                     * An explicit ask — the one shape of queue entry that is never in doubt. It goes
                     * through the session's single summon so that the candidate's OTHER promises (an I-9
                     * hand-off, the see-a-representative screen) cannot add a second row for the same
                     * person; the local `portalHelpSent` still keeps this label from being tapped twice.
                     * It names its control, so the entry reads *talent portal access* [KOP-1g scenario 2].
                     */
                    summonRepresentative(undefined, HANDOFF_CONTROL.talentPortalLink);
                  }}
                  type="button"
                >
                  {t('associate.portalDeflection.action')}
                </button>
              )
            }
          >
            <span className="block font-semibold">{t('associate.portalDeflection.title')}</span>
            <span className="mt-[10px] block">{t('associate.portalDeflection')}</span>
          </Callout>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
